import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import PropertyCard from "@/components/site/PropertyCard";
import BookingWidget from "@/components/site/BookingWidget";
import { properties } from "@/data/properties";
import { landingPages } from "@/data/landingPages";
import Seo, { SITE } from "@/components/site/Seo";
import { breadcrumbJsonLd } from "@/lib/schema";

export default function PropertiesPage() {
  // Smooth-scroll to the stays grid when arriving via /properties#stays (Search button).
  useEffect(() => {
    if (window.location.hash === "#stays") {
      const t = setTimeout(() => {
        document.getElementById("stays")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
      return () => clearTimeout(t);
    }
  }, []);

  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Vacation & Short-Term Rentals in Wilton Manors & Fort Lauderdale",
    url: `${SITE}/properties`,
    about: "Double Rainbow Suites boutique vacation rentals",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: properties.length,
      itemListElement: properties.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE}/properties/${p.slug}`,
        name: p.name,
      })),
    },
  };
  return (
    <main className="pt-32 md:pt-40">
      <Seo
        title="All Stays | Double Rainbow Suites — Wilton Manors & Fort Lauderdale"
        description={`Browse all ${properties.length} boutique short-term & vacation rentals from Double Rainbow Suites in Wilton Manors and Fort Lauderdale, FL — pet-friendly king suites and private-pool homes with instant booking.`}
        canonical={`${SITE}/properties`}
        jsonLd={[
          collectionLd,
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Stays", path: "/properties" },
          ]),
        ]}
      />
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="overline text-sunset mb-5">The collection</p>
          <h1 className="font-display text-5xl md:text-8xl leading-[0.88] tracking-tight max-w-4xl">
            Every stay, in one place.
          </h1>
          <p className="text-lg text-emerald-soft max-w-xl mt-6 font-light">
            {properties.length} curated homes across Wilton Manors and Fort Lauderdale — with more on the way.
          </p>
        </motion.div>

        <div className="mt-12">
          <BookingWidget />
        </div>

        <div id="stays" className="scroll-mt-28 grid md:grid-cols-2 gap-5 md:gap-6 mt-16">
          {properties.map((p, i) => (
            <PropertyCard key={p.slug} property={p} index={i} wide={p.featured} />
          ))}
        </div>

        <nav aria-label="Popular searches" data-testid="stays-popular-searches" className="mt-16 pb-24 md:pb-32 border-t border-emerald/12 pt-10">
          <p className="overline text-sunset mb-5">Popular searches</p>
          <div className="flex flex-wrap gap-3">
            {landingPages.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`stays-landing-${p.slug}`}
                className="inline-flex items-center rounded-full border border-emerald/15 bg-sand-2 px-4 py-2 text-sm text-emerald hover:border-sunset/40 hover:text-sunset transition-colors duration-300"
              >
                {p.h1}
              </Link>
            ))}
          </div>
        </nav>
      </section>
    </main>
  );
}
